import { useState, useEffect, useCallback } from 'react';
import { Timestamp, collection, query, orderBy, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';

export type DeletionRequestStatus = 'pending' | 'confirmed' | 'ready' | 'fulfilled' | 'cancelled';

export interface DeletionRequest {
    uid: string;
    email: string;
    username?: string | null;
    status: DeletionRequestStatus;
    reason?: string;
    stripeId?: string | null;
    intercomId?: string | null;
    intercomEmail?: string | null;
    confirmedViaEmail?: boolean;
    confirmedAt?: Timestamp | null;
    reminderSentAt?: Timestamp | null;
    requestedAt: Timestamp;
    fulfilledAt?: Timestamp | null;
}

export function useDeletionRequests() {
    const [requests, setRequests] = useState<DeletionRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'deletionRequested'), orderBy('requestedAt', 'desc'));
            const snapshot = await getDocs(q);
            const rows: DeletionRequest[] = [];
            snapshot.forEach((d) => rows.push(d.data() as DeletionRequest));
            setRequests(rows);
            setError(null);
        } catch (err) {
            console.error('[DELETION_FETCH] Error:', err);
            setError(err as Error);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const cancelRequest = useCallback(async (uid: string) => {
        const fn = httpsCallable(functions, 'cancelDeletionRequest');
        await fn({ uid });
        await fetchRequests();
    }, [fetchRequests]);

    const pendingCount = requests.filter((r) => r.status === 'pending' || r.status === 'confirmed').length;
    const readyCount = requests.filter((r) => r.status === 'ready').length;

    return { requests, loading, error, cancelRequest, pendingCount, readyCount, fetchRequests };
}

export function useDeletionHelpers() {
    const formatDate = useCallback((timestamp?: Timestamp | null) => {
        if (!timestamp) return '—';
        return timestamp.toDate().toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    }, []);
    return { formatDate };
}
